import React, { CSSProperties } from 'react';
import { Ticket } from './useSupportApi';

type SupportSuccessProps = {
  ticket: Ticket;
  onBackToLobby: () => void;
};

/**
 * Confirmation shown right after a ticket is created — the "Wire Dispatched"
 * screen from the lobby design. Replaces the old behaviour of dropping the
 * player straight into the chat thread.
 */
const SupportSuccess = ({ ticket, onBackToLobby }: SupportSuccessProps) => (
  <div style={containerStyle}>
    {/* Dashed "SENT" seal */}
    <div style={sealStyle}>
      <span style={sealTextStyle}>SENT</span>
    </div>

    <div style={headingStyle}>Wire Dispatched</div>

    <div style={bodyStyle}>
      A deputy will reply by in-app message.
      <br />
      Average response — under two minutes.
    </div>

    {/* Ticket reference chip */}
    <div style={refChipStyle}>{wireRef(ticket.ticketId)}</div>

    <button style={backBtnStyle} onClick={onBackToLobby}>
      Back to Lobby
    </button>
  </div>
);

export default SupportSuccess;

/**
 * Derives a stable, human-friendly 5-digit reference (e.g. WIRE-26918) from the
 * ticket's UUID so the player has something short to quote to support.
 */
function wireRef(ticketId: string): string {
  let hash = 0;
  for (let i = 0; i < ticketId.length; i++) {
    hash = (hash * 31 + ticketId.charCodeAt(i)) >>> 0;
  }
  return `WIRE-${10000 + (hash % 90000)}`;
}

/* ── Styles ── */

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.85rem',
  padding: '1.6rem 1.4rem 1.3rem',
  textAlign: 'center',
};

const sealStyle: CSSProperties = {
  width: '92px',
  height: '92px',
  borderRadius: '50%',
  border: '2px dashed rgba(233,140,38,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '0.2rem',
};

const sealTextStyle: CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 700,
  fontFamily: "'Rye', serif",
  color: '#E98C26',
  letterSpacing: '0.06em',
};

const headingStyle: CSSProperties = {
  fontSize: '1.55rem',
  fontWeight: 700,
  fontFamily: "'Rye', serif",
  color: '#f4e4c1',
  letterSpacing: '0.01em',
};

const bodyStyle: CSSProperties = {
  fontSize: '0.82rem',
  fontFamily: "'Special Elite', monospace",
  color: 'rgba(255,255,255,0.6)',
  lineHeight: 1.6,
  letterSpacing: '0.02em',
};

const refChipStyle: CSSProperties = {
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.8rem',
  fontWeight: 700,
  color: '#E98C26',
  letterSpacing: '0.14em',
  padding: '0.4rem 0.9rem',
  border: '1px dashed rgba(233,140,38,0.55)',
  borderRadius: '7px',
  background: 'rgba(233,140,38,0.06)',
  margin: '0.1rem 0 0.3rem',
};

const backBtnStyle: CSSProperties = {
  width: '100%',
  padding: '0.75rem',
  background: 'linear-gradient(180deg, #f5a030 0%, #E98C26 45%, #c97010 100%)',
  color: '#1a0e06',
  border: 'none',
  borderRadius: '9px',
  fontSize: '1rem',
  fontWeight: 400,
  fontFamily: "'Rye', serif",
  letterSpacing: '0.01em',
  cursor: 'pointer',
  minHeight: '48px',
  boxShadow: '0 4px 18px rgba(233,140,38,0.35)',
};
